import { useEffect, useState } from "react";
import { Cookie, Shield, X } from "lucide-react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";

/**
 * LGPD Cookie Consent Banner.
 *
 * Stores the user's choice in localStorage under "lgpd-cookie-consent":
 *   - "all"       => user accepted all cookies (necessary + analytics + marketing)
 *   - "necessary" => user accepted only strictly-necessary cookies
 *
 * Per LGPD (Lei nº 13.709/2018) and ANPD guidance, we:
 *  - Inform the user clearly about cookie usage
 *  - Allow refusal of non-essential cookies
 *  - Provide a link to the Privacy Policy
 *  - Do NOT pre-check optional categories
 *
 * Strictly-necessary cookies (session, auth token, security) are exempt from
 * consent because the service cannot function without them.
 */

const STORAGE_KEY = "lgpd-cookie-consent";
const STORAGE_TS_KEY = "lgpd-cookie-consent-ts";

type Consent = "all" | "necessary" | null;

export const getCookieConsent = (): Consent => {
  try {
    return (localStorage.getItem(STORAGE_KEY) as Consent) || null;
  } catch {
    return null;
  }
};

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      if (!existing) {
        // small delay to avoid layout jank on first paint
        const t = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(t);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const save = (choice: Exclude<Consent, null>) => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
      localStorage.setItem(STORAGE_TS_KEY, new Date().toISOString());
    } catch { /* ignore */ }
    setVisible(false);
    // Notify the rest of the app (analytics/marketing scripts can listen)
    window.dispatchEvent(new CustomEvent("lgpd-consent-changed", { detail: choice }));
  };

  if (!visible) return null;

  return createPortal(
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Aviso de cookies"
      className="fixed inset-x-0 bottom-0 z-[9998] p-3 sm:p-4 pointer-events-none"
    >
      <div className="mx-auto max-w-3xl tibia-border bg-card rounded-sm shadow-2xl pointer-events-auto">
        <div className="p-4 sm:p-5 flex flex-col gap-3">
          <div className="flex items-start gap-3">
            <Cookie className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h2 className="font-pixel text-[10px] text-primary gold-text-glow mb-2">
                COOKIES E PRIVACIDADE
              </h2>
              <p className="text-xs text-foreground leading-relaxed">
                Usamos cookies essenciais para autenticação, segurança e funcionamento do site.
                Com sua permissão, também usamos cookies analíticos para entender o uso do
                serviço e melhorar sua experiência. Você pode aceitar todos ou apenas os
                necessários. Veja a{" "}
                <Link
                  to="/politica-privacidade"
                  className="text-primary underline hover:opacity-80"
                >
                  Política de Privacidade
                </Link>{" "}
                para detalhes (LGPD).
              </p>
            </div>
            <button
              onClick={() => save("necessary")}
              aria-label="Fechar"
              className="text-muted-foreground hover:text-primary transition-colors shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {showDetails && (
            <div className="tibia-border-inner bg-secondary/20 rounded-sm p-3 text-[11px] text-foreground space-y-2">
              <div>
                <span className="font-pixel text-[8px] text-primary">NECESSÁRIOS</span>
                <p className="text-muted-foreground">
                  Sessão, login, token de autenticação e segurança. Sempre ativos — sem eles
                  o site não funciona.
                </p>
              </div>
              <div>
                <span className="font-pixel text-[8px] text-primary">ANALÍTICOS / FUNCIONAIS</span>
                <p className="text-muted-foreground">
                  Métricas de uso e preferências (ex.: lembrar dialogs já fechados). Opcionais.
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <button
              onClick={() => setShowDetails((v) => !v)}
              className="flex items-center justify-center gap-1.5 text-muted-foreground hover:text-primary transition-colors text-[10px] font-pixel px-3 py-2"
            >
              <Shield className="w-3 h-3" />
              {showDetails ? "Ocultar detalhes" : "Saber mais"}
            </button>
            <button
              onClick={() => save("necessary")}
              className="tibia-btn rounded-sm font-pixel text-[8px] px-4 py-2"
            >
              Apenas necessários
            </button>
            <button
              onClick={() => save("all")}
              className="tibia-btn rounded-sm font-pixel text-[8px] px-4 py-2 bg-primary/20"
            >
              Aceitar todos
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CookieConsentBanner;
